/* Deutsche Zahl-/Währungs-Helfer. */

/** Parst eine deutsche/EN Geldeingabe robust zu einer Zahl.
 *  Deutsch/Spanisch: "." = Tausendertrennung, "," = Dezimaltrennung.
 *  "1.500" -> 1500 ; "1.234,56" -> 1234.56 ; "1,5" -> 1.5 ; "80.50" -> 80.5 ; "1234.56" -> 1234.56
 *  Grundregel: Ein einzelner Punkt mit genau 3 Folgeziffern ist ein Tausenderpunkt
 *  (bei Euro-Beträgen gibt es keine 3 Nachkommastellen); sonst ist er ein Dezimalpunkt. */
export function parseAmount(raw: string | number | null | undefined): number {
  if (raw == null) return 0;
  if (typeof raw === "number") return isFinite(raw) ? raw : 0;
  let s = String(raw).trim();
  if (!s) return 0;
  s = s.replace(/[€\s]/g, "").replace(/[^0-9.,-]/g, "");
  if (!s) return 0;
  // Führendes Vorzeichen separat behandeln, restliche Minus verwerfen
  const sign = s.startsWith("-") ? -1 : 1;
  s = s.replace(/-/g, "");
  if (!s) return 0;

  const hasComma = s.includes(",");
  const hasDot = s.includes(".");

  if (hasComma && hasDot) {
    // Beide vorhanden: das zuletzt stehende Zeichen ist das Dezimaltrennzeichen
    if (s.lastIndexOf(",") > s.lastIndexOf(".")) {
      s = s.replace(/\./g, "").replace(",", "."); // deutsch: 1.234,56
    } else {
      s = s.replace(/,/g, ""); // englisch: 1,234.56
    }
  } else if (hasComma) {
    // Nur Komma -> Dezimaltrennzeichen (1,5 / 1,50 / 1,500 = 1.5);
    // mehrere Kommas -> englische Tausendertrennung (1,000,000)
    const parts = s.split(",");
    s = parts.length > 2 ? parts.join("") : parts.join(".");
  } else if (hasDot) {
    // Nur Punkt -> Tausender (deutsch/spanisch) ODER Dezimalpunkt
    const parts = s.split(".");
    if (parts.length > 2) {
      s = parts.join(""); // mehrere Punkte = eindeutig Tausender: 1.234.567
    } else {
      const [before, after] = parts;
      const istTausender =
        after.length === 3 &&
        before.length >= 1 &&
        before.length <= 3 &&
        !(before.length > 1 && before.startsWith("0")) &&
        before !== "0";
      if (istTausender) s = before + after; // 1.500 -> 1500
      // sonst Dezimalpunkt beibehalten: 1.5 / 80.50 / 0.500
    }
  }
  const n = parseFloat(s);
  return isFinite(n) ? sign * n : 0;
}

/** Formatiert einen Betrag als Euro (de-DE). */
export function formatEUR(n: number, fractionDigits = 0): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(n);
}

export function formatNum(n: number): string {
  return new Intl.NumberFormat("de-DE").format(n);
}

/** Wandelt eine Zahl in einen für Geld-Textfelder vorbefüllbaren String um.
 *  Bewusst OHNE Tausenderpunkt, damit parseAmount den Wert eindeutig zurücklesen kann. */
export function amountToInput(n: number): string {
  return String(Math.round(n * 100) / 100).replace(".", ",");
}

export function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s.trim());
}

/** Akzeptiert deutsche Mobilnummern, +49, Leerzeichen/Bindestriche; min. 7 Ziffern. */
export function isValidPhone(s: string): boolean {
  const digits = s.replace(/[^0-9]/g, "");
  return /^[+0-9 ()/-]{6,}$/.test(s.trim()) && digits.length >= 7 && digits.length <= 15;
}

export function isValidPLZ(s: string): boolean {
  return /^[0-9]{5}$/.test(s.trim());
}

/** Alter in Jahren aus ISO-Geburtsdatum. */
export function ageFromISO(iso: string): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}
